// Menu au clic droit sur une tâche : les actions courantes sans ouvrir la fiche.
// La suppression n'est jamais derrière un clic gauche, et reste annulable 5 s.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { addDays, format, nextMonday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import {
  useUpdateTask,
  type Task,
} from '../../hooks/queries/useTasks';
import {
  useActiveCollaborators,
  useAssignTask,
  useTaskAssignees,
  useUnassignTask,
} from '../../hooks/queries/useTaskCollaboration';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useUndoableTaskDelete } from '../../hooks/useUndoableTaskDelete';
import { clearDue, mergeDueDate } from '../../lib/tasks/taskReschedule';
import { taskDeepLinkPath } from '../../lib/tasks/taskDeepLink';
import { Avatar } from '../ui/Avatar';
import { DatePicker } from '../ui/DatePicker';
import {
  FaCalendarClock,
  FaCheck,
  FaChevronRight,
  FaCircleCheck,
  FaFlag,
  FaLink,
  FaPenToSquare,
  FaTrashCan,
  FaUserPlus,
  type AppIcon,
} from '../ui/FaIcons';

export interface TaskMenuTarget {
  task: Task;
  x: number;
  y: number;
}

/** État du menu pour une liste : `open` se branche sur `onContextMenu` d'une ligne. */
export function useTaskContextMenu() {
  const [target, setTarget] = useState<TaskMenuTarget | null>(null);
  return {
    target,
    open: (event: React.MouseEvent, task: Task) => {
      event.preventDefault();
      setTarget({ task, x: event.clientX, y: event.clientY });
    },
    /** Pour un conteneur : ouvre le menu de la carte `data-task-id` sous le pointeur (kanban, calendrier). */
    openFrom: (event: React.MouseEvent, tasks: Task[]) => {
      const id = (event.target as HTMLElement).closest<HTMLElement>('[data-task-id]')?.dataset.taskId;
      const task = id ? tasks.find((t) => t.id === id) : undefined;
      if (!task) return;
      event.preventDefault();
      setTarget({ task, x: event.clientX, y: event.clientY });
    },
    close: () => setTarget(null),
  };
}

const PRIORITIES = [
  { value: 1, label: 'P1 · Urgente' },
  { value: 2, label: 'P2 · Haute' },
  { value: 3, label: 'P3 · Normale' },
  { value: 4, label: 'P4 · Basse' },
];

const WIDTH = 236;
const SUB_WIDTH = 220;
const isoDay = (date: Date) => format(date, 'yyyy-MM-dd');

export function TaskContextMenu({
  target,
  onClose,
  onOpenTask,
}: {
  target: TaskMenuTarget;
  onClose: () => void;
  /** Ouvre la fiche de la tâche (même effet que le clic gauche). */
  onOpenTask: (task: Task) => void;
}) {
  const { task } = target;
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: target.x, top: target.y, flip: false });
  const canEdit = useCanAccessFeature('tasks_edit');
  const canAssign = useCanAccessFeature('tasks_assign');
  const canDelete = useCanAccessFeature('tasks_delete');
  const { priority: priorityEnabled } = useEffectiveTaskFields();
  const completion = useTaskCompletion();
  const update = useUpdateTask();
  const assign = useAssignTask();
  const unassign = useUnassignTask();
  const requestDelete = useUndoableTaskDelete();
  const { data: assignees = [] } = useTaskAssignees(task.id);
  const { data: collaborators = [] } = useActiveCollaborators();
  const done = !!task.is_completed;
  const [picking, setPicking] = useState(false);

  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? 320;
    const left = Math.max(8, Math.min(target.x, window.innerWidth - WIDTH - 12));
    setPos({
      left,
      top: Math.max(8, Math.min(target.y, window.innerHeight - height - 12)),
      flip: left + WIDTH + SUB_WIDTH > window.innerWidth,
    });
  }, [target]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Le calendrier vit dans un portail hors du menu : il en fait partie.
    const inside = (node: EventTarget | null) =>
      node instanceof Element &&
      (ref.current?.contains(node) || node.closest('[data-radix-popper-content-wrapper]') !== null);
    const onDown = (e: MouseEvent) => {
      if (!inside(e.target)) onClose();
    };
    const onScroll = (e: Event) => {
      if (!inside(e.target)) onClose();
    };
    // La molette au-dessus du menu ne fait défiler que ses listes, jamais la page derrière.
    const onWheel = (e: WheelEvent) => {
      const list = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-menu-scroll]') : null;
      if (!list) {
        e.preventDefault();
        return;
      }
      const atTop = list.scrollTop <= 0 && e.deltaY < 0;
      const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1 && e.deltaY > 0;
      if (atTop || atBottom) e.preventDefault();
    };
    const node = ref.current;
    node?.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      node?.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const fail = (err: unknown) => toast.error(err instanceof Error ? err.message : 'Action impossible');
  const run = (action: () => void) => () => {
    action();
    onClose();
  };

  function reschedule(day: string | null, label: string) {
    update.mutate(
      { taskId: task.id, updates: day ? mergeDueDate(task, day) : clearDue() },
      { onSuccess: () => toast.success(label), onError: fail },
    );
  }

  function toggleAssignee(id: string) {
    const mutation = assignees.some((p) => p.id === id) ? unassign : assign;
    mutation.mutate({ taskId: task.id, assigneeId: id }, { onError: fail });
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.origin + taskDeepLinkPath(task.id));
      toast.success('Lien de la tâche copié');
    } catch {
      toast.error('Impossible de copier le lien');
    }
  }

  const now = new Date();
  const tomorrow = addDays(now, 1);
  const monday = nextMonday(now);
  const inAWeek = addDays(now, 7);
  const short = (date: Date) => format(date, 'EEE d MMM', { locale: fr });

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={`Actions sur la tâche ${task.content}`}
      data-cime-modal
      onContextMenu={(e) => e.preventDefault()}
      className="fixed z-[70] rounded-xl border border-neutral-200 bg-white p-1.5 text-[13px] shadow-[0_16px_40px_rgba(16,24,20,0.16)]"
      style={{ left: pos.left, top: pos.top, width: WIDTH }}
    >
      <p className="mb-1 truncate border-b border-neutral-100 px-2.5 pb-2 pt-1.5 text-xs text-cime-text-muted">
        {task.content}
      </p>
      <Item icon={FaPenToSquare} onClick={run(() => onOpenTask(task))}>
        Ouvrir la fiche
      </Item>
      {canEdit ? (
        <Item
          icon={FaCircleCheck}
          onClick={run(() => completion.toggle(task))}
        >
          {done ? 'Rouvrir la tâche' : 'Marquer comme terminée'}
        </Item>
      ) : null}

      {canEdit || canAssign ? <Rule /> : null}
      {canEdit ? (
        <Sub icon={FaCalendarClock} label="Reporter" flip={pos.flip} forceOpen={picking}>
          <Item hint={short(tomorrow)} onClick={run(() => reschedule(isoDay(tomorrow), 'Échéance reportée à demain'))}>
            Demain
          </Item>
          <Item hint={short(monday)} onClick={run(() => reschedule(isoDay(monday), 'Échéance reportée à lundi'))}>
            Lundi prochain
          </Item>
          <Item hint="+7 j" onClick={run(() => reschedule(isoDay(inAWeek), 'Échéance reportée d’une semaine'))}>
            Dans une semaine
          </Item>
          <Rule />
          {picking ? (
            <DatePicker
              defaultOpen
              required
              value={(task.due_date ?? task.due_datetime ?? '').slice(0, 10)}
              onChange={(day) => {
                if (!day) return;
                reschedule(day, 'Échéance modifiée');
                onClose();
              }}
              onOpenChange={(open) => {
                if (!open) setPicking(false);
              }}
              aria-label="Choisir une date"
              trigger={
                <span className="flex w-full items-center rounded-[7px] bg-[#fbfaf7] px-2.5 py-[7px] text-cime-primary">
                  Choisir une date…
                </span>
              }
            />
          ) : (
            <Item onClick={() => setPicking(true)}>Choisir une date…</Item>
          )}
          {task.due_date || task.due_datetime ? (
            <Item onClick={run(() => reschedule(null, 'Échéance retirée'))}>Retirer l’échéance</Item>
          ) : null}
        </Sub>
      ) : null}
      {canAssign ? (
        <Sub icon={FaUserPlus} label="Assigner à" flip={pos.flip}>
          <div data-menu-scroll className="max-h-64 overflow-y-auto overscroll-contain">
            {collaborators.map((person) => {
              const on = assignees.some((p) => p.id === person.id);
              return (
                <Item key={person.id} checked={on} onClick={() => toggleAssignee(person.id)}>
                  <span className="flex min-w-0 items-center gap-2">
                    <Avatar name={person.full_name} src={person.avatar_url} size="xs" className="h-5 w-5 text-[10px]" />
                    <span className="truncate">{person.full_name}</span>
                  </span>
                </Item>
              );
            })}
          </div>
        </Sub>
      ) : null}
      {canEdit && priorityEnabled ? (
        <Sub icon={FaFlag} label="Priorité" flip={pos.flip}>
          {PRIORITIES.map((p) => (
            <Item
              key={p.value}
              checked={(task.priority ?? 3) === p.value}
              onClick={run(() => update.mutate({ taskId: task.id, updates: { priority: p.value } }, { onError: fail }))}
            >
              {p.label}
            </Item>
          ))}
        </Sub>
      ) : null}
      <Item icon={FaLink} onClick={run(() => void copyLink())}>
        Copier le lien
      </Item>

      {canDelete ? (
        <>
          <Rule />
          <Item icon={FaTrashCan} danger onClick={run(() => requestDelete({ id: task.id, content: task.content }))}>
            Supprimer
          </Item>
        </>
      ) : null}
    </div>,
    document.body,
  );
}

function Rule() {
  return <hr className="my-1 border-0 border-t border-neutral-100" />;
}

function Item({
  icon: Icon,
  hint,
  checked,
  danger,
  onClick,
  children,
}: {
  icon?: AppIcon;
  hint?: string;
  checked?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'group/item flex w-full items-center gap-2.5 rounded-[7px] px-2.5 py-[7px] text-left transition-colors',
        danger
          ? 'text-[#c8431f] hover:bg-[#fdece6]'
          : 'text-cime-text-primary hover:bg-[#fbfaf7] hover:text-cime-primary',
      )}
    >
      {Icon ? (
        <Icon
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0',
            danger ? 'text-[#c8431f]' : 'text-neutral-400 group-hover/item:text-cime-primary',
          )}
          aria-hidden
        />
      ) : null}
      <span className="min-w-0 flex-1">{children}</span>
      {hint ? <small className="flex-shrink-0 text-[11px] text-neutral-400">{hint}</small> : null}
      {checked ? <FaCheck className="h-3 w-3 flex-shrink-0 text-cime-primary" aria-hidden /> : null}
    </button>
  );
}

function Sub({
  icon: Icon,
  label,
  flip,
  forceOpen = false,
  children,
}: {
  icon: AppIcon;
  label: string;
  /** Ouvre le sous-menu à gauche quand la place manque à droite. */
  flip: boolean;
  /** Garde le sous-menu ouvert (calendrier affiché). */
  forceOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="group/sub relative">
      <div
        role="menuitem"
        aria-haspopup="menu"
        tabIndex={0}
        className="flex w-full cursor-default items-center gap-2.5 rounded-[7px] px-2.5 py-[7px] text-cime-text-primary group-hover/sub:bg-[#fbfaf7] group-hover/sub:text-cime-primary group-focus-within/sub:bg-[#fbfaf7]"
      >
        <Icon className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400 group-hover/sub:text-cime-primary" aria-hidden />
        <span className="flex-1">{label}</span>
        <FaChevronRight className="h-2.5 w-2.5 text-neutral-400" aria-hidden />
      </div>
      <div
        role="menu"
        aria-label={label}
        className={cn(
          'absolute -top-1.5 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-[0_16px_40px_rgba(16,24,20,0.16)] group-hover/sub:block group-focus-within/sub:block',
          forceOpen ? 'block' : 'hidden',
          flip ? 'right-[calc(100%-2px)]' : 'left-[calc(100%-2px)]',
        )}
        style={{ width: SUB_WIDTH }}
      >
        {children}
      </div>
    </div>
  );
}
